import { LazyLoadEvent } from 'primeng/api';
import { DatagridService } from '../../widgets';
import { Input, EventEmitter, Output, Directive } from '@angular/core';
import { PagedResultGeneric } from '../..';
import { PagedSearch } from '../../model';
import { timeoutWrapper } from '../../decorator/timeout-wrapper';

@Directive()
export abstract class LazySelectableTableComponent<TSearchParams extends PagedSearch, TModel> {
  @Input()
  get searchParams() {
    return this._searchParams;
  }
  set searchParams(value: TSearchParams) {
    this._searchParams = value;
    if (!this.preventSelectAllReset) {
      this.reset();
    }

    if (!this._searchParams.PageSize) {
      this._searchParams.PageSize = 20;
    }

    this.searchParamsChange.emit(this._searchParams);
  }
  @Output() searchParamsChange: EventEmitter<TSearchParams> = new EventEmitter();
  @Input() preventInitialLoad = false;
  @Input() preventSelectAllReset = false;
  searchResult: PagedResultGeneric<TModel>;
  selectedMap: Map<any, boolean> = new Map();
  loading = false;
  selectAll = false;
  selectedCount = 0;
  loadingAllSelected: boolean = false;

  private _searchParams: TSearchParams;
  /**
   * Required properties/services to set for this abstract class.
   * @param _modelKey - String property key for TModel (e.g. ID)
   * @param _datagridService - Datagrid service
   */
  constructor(private _modelKey: string, private _datagridService: DatagridService) {}

  /**
   * Should simply return an API call for fetching the results to be used in search()
   */
  abstract fetchResults(): Promise<PagedResultGeneric<TModel>>;

  public search(): Promise<PagedResultGeneric<TModel>> {
    this.loading = true;
    return this.fetchResults()
      .then((result) => {
        this.searchParams.TotalCount = result.TotalCount;
        this.searchResult = result;
        if (this.selectAll) {
          result.Result?.forEach((item) => {
            if (!this.hasItem(item)) {
              this.setItem(item, true, false);
            }
          });
        }

        if (result.TotalCount === 0) {
          this.searchResult.Result = [];
          this.searchResult.TotalPages = 1;
          this.searchResult.CurrentPageNumber = 1;
        }
        return result;
      })
      .finally(() => (this.loading = false));
  }

  public reset() {
    this.selectedMap = new Map();
    this.selectAll = false;
    this.selectedCount = 0;
  }

  @timeoutWrapper()
  lazyLoad(event: LazyLoadEvent): Promise<PagedResultGeneric<TModel>> {
    if (this.preventInitialLoad) {
      this.preventInitialLoad = false;
      return;
    }
    this._datagridService.mapDatagridPagingToPagedSearch(event, this.searchParams);
    return this.search();
  }

  onRowSelectionChange(item: TModel): void {
    const key = item[this._modelKey];
    if (this.selectedMap.has(key)) {
      const value = this.selectedMap.get(key);
      this.setItem(item, !value);
    } else {
      this.setItem(item, true);
    }
  }

  onSelectAllChange(): void {
    if (this.selectAll) {
      this.searchResult?.Result?.forEach((result) => {
        this.setItem(result, true, false);
      });
      this.selectedCount = this.searchResult?.TotalCount ?? 0;
    } else {
      this.selectedCount = 0;
      this.selectedMap.clear();
    }
  }

  itemIsSelected(item: TModel): boolean {
    return this.selectedMap.has(item[this._modelKey]) && this.selectedMap.get(item[this._modelKey]);
  }

  protected fetchAllSelectedIds<T>(): Promise<T[]> {
    if (!this.selectAll) {
      const ids = [];
      for (const pair of this.selectedMap.entries()) {
        if (pair[1]) {
          ids.push(pair[0]);
        }
      }
      return Promise.resolve(ids);
    } else {
      this.loadingAllSelected = true;
      this.loading = true;
      const copiedParams: TSearchParams = JSON.parse(JSON.stringify(this.searchParams));
      this.searchParams.PageSize = 9999;
      return this.fetchResults()
        .then((result) => {
          let ids = result.Result.map<T>((x) => x[this._modelKey]);
          // filter out any deselected IDs
          ids = ids.filter((x) => !this.selectedMap.has(x) || this.selectedMap.get(x));
          return ids;
        })
        .finally(() => {
          this.searchParams = copiedParams;
          this.loadingAllSelected = false;
          this.loading = false;
        });
    }
  }

  protected getIdsByCheckedState(checkState: boolean = true) {
    let ids = [];
    this.selectedMap.forEach((x, y) => {
      if (x == checkState) {
        ids.push(y);
      }
    });
    return ids;
  }

  private hasItem(item: TModel) {
    return this.selectedMap.has(item[this._modelKey]);
  }

  private setItem(item: TModel, selected: boolean, adjustCount = true) {
    this.selectedMap.set(item[this._modelKey], selected);
    if (adjustCount) {
      if (selected) {
        this.selectedCount += 1;
      } else {
        this.selectedCount -= 1;
      }
    }
  }
}
